// src/components/resources/tools/generators/AdMockup/TikTokAd.tsx
'use client'

import {
  PhotoIcon,
  ChatBubbleOvalLeftIcon,
  ArrowUturnRightIcon,
  BookmarkIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline'
import {
  HeartIcon as HeartSolidIcon,
  CheckBadgeIcon as CheckBadgeSolidIcon,
} from '@heroicons/react/24/solid'
import { Avatar } from './Avatar'
import { formatCount, handleOf } from './ad-format'
import type { AdState } from './types'

/**
 * TikTok in-feed ad (9:16 full-screen). Real TikTok ads render the creative
 * full-bleed with the right-rail action stack (profile + follow, like, comment,
 * bookmark, share, spinning record), the bottom-left author/caption/music
 * ticker, the "Sponsored" marker, and a full-width CTA bar pinned to the bottom.
 */
export function TikTokAd({ state, creativeImage, avatarImage }: { state: AdState; creativeImage: string | null; avatarImage: string | null }): JSX.Element {
  const handle = handleOf(state.pageName)
  const rail = 'flex flex-col items-center gap-0.5'
  const railCount = 'text-[11px] font-semibold drop-shadow'
  return (
    <div className="relative w-full overflow-hidden rounded-[28px] bg-black font-sans text-white" style={{ paddingBottom: `${(16 / 9) * 100}%` }}>
      {creativeImage ? (
        // eslint-disable-next-line @next/next/no-img-element -- user-uploaded data URL; next/image can't optimize data URIs and html2canvas needs the raw <img>
        <img src={creativeImage} alt={`${state.pageName} ad creative`} className="absolute inset-0 h-full w-full object-cover" />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-gradient-to-br from-slate-800 via-slate-900 to-black text-white/70">
          <PhotoIcon className="h-10 w-10" />
          <span className="text-xs">Upload your vertical creative (9:16)</span>
        </div>
      )}
      {/* top tabs */}
      <div className="absolute inset-x-0 top-3 flex items-center justify-center gap-4 text-[13px] font-semibold drop-shadow">
        <span className="text-white/60">Following</span>
        <span className="border-b-2 border-white pb-0.5">For You</span>
      </div>

      {/* right action rail — the music disc (last item) sits just above the
          caption block; anchoring lower keeps the stack tight to the caption
          instead of floating with dead space beneath the disc. */}
      <div className="absolute bottom-[13%] right-1.5 flex flex-col items-center gap-3">
        <div className="relative mb-1.5">
          <Avatar name={state.pageName} image={avatarImage} size={38} />
          <span className="absolute -bottom-1.5 left-1/2 flex h-4 w-4 -translate-x-1/2 items-center justify-center rounded-full bg-[#FE2C55] text-[11px] leading-none text-white">+</span>
        </div>
        <div className={rail}><HeartSolidIcon className="h-[26px] w-[26px] drop-shadow" /><span className={railCount}>{formatCount(state.likes)}</span></div>
        <div className={rail}><ChatBubbleOvalLeftIcon className="h-[26px] w-[26px] -scale-x-100 drop-shadow" /><span className={railCount}>{formatCount(state.comments)}</span></div>
        <div className={rail}><BookmarkIcon className="h-[26px] w-[26px] drop-shadow" /><span className={railCount}>{formatCount(Math.round(state.likes * 0.12))}</span></div>
        <div className={rail}><ArrowUturnRightIcon className="h-[26px] w-[26px] drop-shadow" /><span className={railCount}>{formatCount(state.shares)}</span></div>
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-zinc-600 to-zinc-900 ring-2 ring-black">
          <span className="text-sm" aria-hidden>♪</span>
        </div>
      </div>

      {/* bottom author + caption + music + CTA */}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-3 pb-4 pt-12">
        <div className="mb-2 pr-14">
          <div className="flex items-center gap-1 text-[15px] font-semibold drop-shadow">
            @{handle}
            {state.verified && <CheckBadgeSolidIcon className="h-4 w-4 text-[#20D5EC]" aria-label="Verified" />}
            <span className="ml-1 rounded-sm bg-white/15 px-1 py-px text-[10px] font-medium">Sponsored</span>
          </div>
          {state.primaryText.trim() && <p className="mt-1 line-clamp-2 text-[13px] leading-snug drop-shadow">{state.primaryText}</p>}
          <div className="mt-1 flex items-center gap-1 text-[12px] drop-shadow"><span aria-hidden>♪</span> original sound — {state.pageName}</div>
        </div>
        <span className="flex items-center justify-center gap-1 rounded-md bg-white py-2 text-sm font-semibold text-black">
          {state.cta}
          <ChevronRightIcon className="h-4 w-4" />
        </span>
      </div>
    </div>
  )
}
